import { useEffect, useRef, useState } from 'react';

import { radius, space } from './theme';

/**
 * The inline label editor.
 *
 * A real `<textarea>` positioned over the canvas, not a canvas-drawn caret.
 *
 * ## Why that is not a shortcut
 *
 * Input methods. A Vietnamese, Japanese, Korean or Chinese user types through a system IME
 * that composes over several keystrokes and shows its own candidate window. A canvas reading
 * `keydown` cannot see a composition — it receives the raw keys and produces nonsense, or
 * nothing. Only a focused form control participates, and getting one right is not something
 * to reimplement: selection, the platform's editing shortcuts, autocorrect, spellcheck,
 * accessibility and the on-screen keyboard all come with it.
 *
 * The cost is that the editor is a DOM element in front of the drawing rather than part of
 * it, so it is styled to sit as close to the painted result as it can — same font, same size,
 * same place — and it disappears the moment it loses focus.
 *
 * ## Committing
 *
 * On a press anywhere outside, on blur, on Escape, and on ⌘/Ctrl+Enter. **Not** on plain
 * Enter, which inserts a newline: a label may be several lines, and the alternative would make
 * a multi-line label impossible. Escape reverts, which is the one ending a user reaches for
 * without thinking.
 *
 * ## Why blur alone was not enough
 *
 * It should have been, and the reason it was not is three layers away: the canvas calls
 * `preventDefault()` on `pointerdown`, which is what stops the browser starting a text-drag
 * selection across the page — and which also stops it moving focus. So clicking the drawing
 * left the editor open, still focused, still showing its selected text, with no obvious way to
 * put it away. The user is left holding an input that will not close.
 *
 * The listener below therefore watches for a press outside and commits directly, rather than
 * relying on a focus change that the canvas has deliberately suppressed. It runs in the
 * **capture** phase so it sees the press before the canvas does, which is what lets that first
 * press mean *close this* and nothing else.
 *
 * ## The symbol palette
 *
 * `\alpha` has always worked. Nothing said so, which for the notation a teacher needs most —
 * the Greek letter on an angle — meant it may as well not have. Documentation does not fix
 * that: the moment of need is with the caret blinking on the canvas, not in a guide.
 *
 * The buttons insert the **character**, not the command: `α`, not `\alpha`. The markup engine
 * passes a character it does not recognise straight through, so both reach the canvas the same
 * way — but only one of them shows the user what they are getting while they type. The two
 * commands that have no character, `_{}` and `^{}`, are the exception, and they put the caret
 * between the braces so the next keystroke lands where it belongs.
 *
 * Pressing one must not close the editor, so each button suppresses the default on
 * `pointerdown`: without it the press moves focus, `blur` fires, and the label commits before
 * the symbol is inserted.
 */

export interface TextEditorProps {
  /** Where the label sits, in screen coordinates. */
  readonly at: { readonly x: number; readonly y: number };
  readonly initial: string;
  readonly fontPx: number;
  readonly colour: string;
  readonly onCommit: (content: string) => void;
  readonly onCancel: () => void;
}

/**
 * What a geometry label actually reaches for.
 *
 * Two rows, not a symbol table. The full set MathLive offers is thousands of entries and this
 * floats over the drawing the user is trying to see — a palette they have to scan costs more
 * attention than typing the command would have. These are the ones that appear on a diagram:
 * the Greek an angle is named with, and the relations a caption states.
 */
const PALETTE: readonly (readonly string[])[] = [
  ['α', 'β', 'γ', 'δ', 'θ', 'λ', 'μ', 'π', 'φ', 'ω'],
  ['∠', '△', '⊥', '∥', '°', '′', '≈', '≅', '⇒', '∞'],
];

/** The two that are layout rather than a character, so they arrive as markup. */
const SCRIPTS: readonly { readonly label: string; readonly insert: string }[] = [
  { label: 'x₁', insert: '_{}' },
  { label: 'x²', insert: '^{}' },
];

export function TextEditor({ at, initial, fontPx, colour, onCommit, onCancel }: TextEditorProps) {
  const [draft, setDraft] = useState(initial);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const committed = useRef(false);
  /*
   * Where to put the caret after an insertion.
   *
   * React owns the value, so the text is not in the DOM until after the re-render — setting
   * `selectionStart` during the handler would place it in the *previous* string and the caret
   * would jump to the end. The effect below applies it once the new text is committed.
   */
  const caret = useRef<number | null>(null);

  /*
   * The draft is mirrored into a ref so the outside-press listener can read the current text.
   *
   * The listener is registered once, and a closure over `draft` would capture the value as it
   * was on that render — committing whatever the label said before the user typed anything.
   */
  const latest = useRef(initial);
  const handlers = useRef({ onCommit, onCancel });

  // Written after the render rather than during it: a ref read or written mid-render is
  // exactly the pattern that breaks under concurrent rendering, and the linter says so.
  useEffect(() => {
    latest.current = draft;
    handlers.current = { onCommit, onCancel };
  });

  useEffect(() => {
    const field = ref.current;
    if (field === null) return;
    field.focus();
    field.select();
  }, []);

  useEffect(() => {
    const position = caret.current;
    const field = ref.current;
    if (position === null || field === null) return;

    caret.current = null;
    field.setSelectionRange(position, position);
    field.focus();
  }, [draft]);

  useEffect(() => {
    const onOutsidePress = (event: PointerEvent): void => {
      // The palette counts as inside: a press on it must insert a symbol, not commit the
      // label and take the editor away mid-word.
      const field = box.current;
      if (field === null) return;
      if (event.target instanceof Node && field.contains(event.target)) return;

      if (committed.current) return;
      committed.current = true;

      /*
       * Swallowed, so this press only closes the editor.
       *
       * Letting it through as well would make one click do two things — dismiss the editor
       * *and* select or start dragging whatever happened to be underneath. That is a
       * surprise every time, and it is worst in the case it is most likely: the user clicks
       * an empty part of the canvas to get rid of the input and instead begins a marquee.
       */
      event.preventDefault();
      event.stopPropagation();

      handlers.current.onCommit(latest.current);
    };

    globalThis.addEventListener('pointerdown', onOutsidePress, { capture: true });
    return () => {
      globalThis.removeEventListener('pointerdown', onOutsidePress, { capture: true });
    };
  }, []);

  const finish = (content: string): void => {
    // Blur fires after Escape and after a keyboard commit, so the guard keeps the label from
    // being written twice — the second write would be a second undo step for one edit.
    if (committed.current) return;
    committed.current = true;
    onCommit(content);
  };

  /**
   * Puts `text` in at the caret.
   *
   * A selection is *wrapped* rather than replaced when the insertion has a `{}` in it: typing
   * `A`, selecting the `1` and pressing `x₁` should give `A_{1}`, which is what a user who
   * has already typed the digit expects. Replacing it would silently delete their work.
   */
  const insert = (text: string): void => {
    const field = ref.current;
    if (field === null) return;

    const start = field.selectionStart;
    const end = field.selectionEnd;
    const selected = draft.slice(start, end);

    const brace = text.indexOf('{}');
    const body = brace === -1 ? text : `${text.slice(0, brace + 1)}${selected}}`;

    caret.current = brace === -1 ? start + body.length : start + brace + 1 + selected.length;
    setDraft(draft.slice(0, start) + body + draft.slice(end));
  };

  return (
    <div
      ref={box}
      style={{
        position: 'absolute',
        left: at.x,
        top: at.y - fontPx,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        zIndex: 20,
      }}
    >
      <textarea
        ref={ref}
        value={draft}
        aria-label="Label text"
        spellCheck={false}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          finish(draft);
        }}
        onKeyDown={(event) => {
          // Never let a keystroke meant for the label reach the canvas shortcuts.
          event.stopPropagation();

          if (event.key === 'Escape') {
            committed.current = true;
            onCancel();
            return;
          }

          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            finish(draft);
          }
        }}
        style={{
          minWidth: 96,
          minHeight: fontPx * 1.6,
          padding: '3px 6px',
          margin: 0,
          resize: 'none',
          overflow: 'hidden',
          borderRadius: radius.xs,
          border: `1px solid var(--es-accent)`,
          outline: 'none',
          background: 'rgba(255,255,255,0.98)',
          // The accent ring the rest of the interface uses for focus: this *is* focus.
          boxShadow: '0 0 0 3px var(--es-accent-softer), 0 2px 8px rgb(15 23 42 / 12%)',
          // Matched to the painted label so the text does not jump on commit.
          font: `${String(fontPx)}px var(--es-font)`,
          lineHeight: 1.28,
          color: colour,
          whiteSpace: 'pre',
        }}
      />

      <Palette onInsert={insert} />
    </div>
  );
}

/**
 * The strip of symbols under the field.
 *
 * Sized in fixed pixels rather than in the label's font size: it is a control, and a control
 * that grows with a 40-point title becomes a wall across the drawing. It reads at 13px whatever
 * the label is set in.
 */
function Palette({ onInsert }: { readonly onInsert: (text: string) => void }) {
  return (
    <div
      className="es-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.hair,
        padding: space.tight,
      }}
    >
      {PALETTE.map((row) => (
        <div key={row.join('')} style={{ display: 'flex', gap: 2 }}>
          {row.map((symbol) => (
            <Key
              key={symbol}
              label={symbol}
              title={symbol}
              onPress={() => {
                onInsert(symbol);
              }}
            />
          ))}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 2 }}>
        {SCRIPTS.map((script) => (
          <Key
            key={script.insert}
            label={script.label}
            title={script.insert === '_{}' ? 'Chỉ số dưới' : 'Số mũ'}
            onPress={() => {
              onInsert(script.insert);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function Key({
  label,
  title,
  onPress,
}: {
  readonly label: string;
  readonly title: string;
  readonly onPress: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      /*
       * The press is suppressed, not handled here: letting it through moves focus off the
       * textarea, which fires `blur`, which commits the label — so the symbol would be
       * inserted into an editor that had already closed.
       */
      onPointerDown={(event) => {
        event.preventDefault();
      }}
      onClick={onPress}
      className="es-btn"
      style={{ width: 23, height: 23, padding: 0, fontSize: 13 }}
    >
      {label}
    </button>
  );
}
